import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, ChevronLeft, Download, Save, Search } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { setLocaleString } from '@/lib/adapters'
import { catalogEntries } from './catalogEntries'

const CAP = 150

/**
 * A22.04 — Tarjima muharriri. Klient i18n katalogini tahrirlab, o'zgargan kalitlarni
 * (ru/uz) JONLI backend-store'ga saqlaysiz (`PUT /admin/localization/strings`, audited)
 * yoki offline «patch» (JSON) qilib yuklab olasiz. Backend: locale_strings.
 */
export function LocalizationEditor() {
  const t = useT()
  const [q, setQ] = useState('')
  const [edits, setEdits] = useState<Record<string, { ru: string; uz: string }>>({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const all = useMemo(() => catalogEntries().filter((e) => !e.fn), [])
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return all.slice(0, CAP)
    return all
      .filter((e) => e.key.toLowerCase().includes(s) || e.en.toLowerCase().includes(s))
      .slice(0, CAP)
  }, [all, q])

  const editCount = Object.keys(edits).length

  const set = (key: string, base: { ru: string; uz: string }, lang: 'ru' | 'uz', val: string) => {
    setEdits((prev) => {
      const cur = prev[key] ?? { ...base }
      const next = { ...cur, [lang]: val }
      // Drop the entry if it matches the original again (no real change).
      if (next.ru === base.ru && next.uz === base.uz) {
        const { [key]: _drop, ...rest } = prev
        return rest
      }
      return { ...prev, [key]: next }
    })
  }

  const saveToServer = async () => {
    if (editCount === 0 || saving) return
    setSaving(true)
    setSaved(false)
    try {
      // Persist every edited key for both languages to the server store.
      await Promise.all(
        Object.entries(edits).flatMap(([key, v]) => [
          setLocaleString('ru', key, v.ru),
          setLocaleString('uz', key, v.uz),
        ]),
      )
      setEdits({})
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2500)
    } catch {
      /* keep edits so the operator can retry */
    } finally {
      setSaving(false)
    }
  }

  const downloadPatch = () => {
    if (editCount === 0) return
    const url = URL.createObjectURL(new Blob([JSON.stringify(edits, null, 2)], { type: 'application/json' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'grom-i18n-patch.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  const inputCls =
    'w-full rounded border border-border-default bg-surface-2 px-2 py-1 text-sm text-primary outline-none focus:border-accent'

  return (
    <>
      <PageHeader
        title={t.loc.editorTitle}
        screenId="A22.04"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="flex items-center gap-2 border-b border-border-subtle p-3">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5">
          <Search size={14} className="text-tertiary" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.loc.searchPh}
            className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
        </div>
        <button
          onClick={saveToServer}
          disabled={editCount === 0 || saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {saved ? <Check size={14} /> : <Save size={14} />}
          {saving ? t.common.saving : saved ? t.common.copied : t.common.save}
          {editCount > 0 && !saved && ` (${editCount})`}
        </button>
        <button
          onClick={downloadPatch}
          disabled={editCount === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-primary hover:bg-surface-3 disabled:opacity-50"
        >
          <Download size={14} /> {t.loc.downloadPatch} {editCount > 0 && `(${editCount})`}
        </button>
      </div>
      <p className="border-b border-border-subtle px-3 py-2 text-xs text-tertiary">{t.loc.editorNote}</p>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="overflow-hidden rounded-md border border-border-default">
          <div className="grid grid-cols-[minmax(120px,0.8fr)_1fr_1fr_1fr] gap-3 border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            <span>{t.loc.keys}</span>
            <span>EN</span>
            <span>RU</span>
            <span>UZ</span>
          </div>
          {filtered.map((e) => {
            const cur = edits[e.key]
            return (
              <div
                key={e.key}
                className="grid grid-cols-[minmax(120px,0.8fr)_1fr_1fr_1fr] items-center gap-3 border-b border-border-subtle px-3 py-1.5 last:border-0"
              >
                <span className="truncate font-mono text-xs text-accent" title={e.key}>{e.key}</span>
                <span className="truncate text-sm text-tertiary" title={e.en}>{e.en}</span>
                <input className={inputCls} value={cur?.ru ?? e.ru} onChange={(ev) => set(e.key, { ru: e.ru, uz: e.uz }, 'ru', ev.target.value)} />
                <input className={inputCls} value={cur?.uz ?? e.uz} onChange={(ev) => set(e.key, { ru: e.ru, uz: e.uz }, 'uz', ev.target.value)} />
              </div>
            )
          })}
        </div>
        {all.length > CAP && <p className="mt-2 text-center text-xs text-tertiary">{t.loc.showingCap(filtered.length, all.length)}</p>}
      </div>
    </>
  )
}
